import React, { MouseEventHandler, useCallback, useState } from 'react';
import { DropdownList } from '../../dropdown/DropdownList/DropdownList';
import {
  DropdownPopper,
  DropdownPopperProps
} from '../../dropdown/DropdownPopper/DropdownPopper';
import { Icon, IconSize, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import {
  ButtonStandard,
  ButtonStandardProps
} from '../ButtonStandard/ButtonStandard';
import styles from './DropdownButton.module.css';

export type DropdownButtonProps<T> = ButtonStandardProps & {
  items: Array<T>;
  getDisplayTextOfItem: (item: T) => string;
  getKeyOfItem: (item: T, index: number) => string;
  /**
   * the item will be null if no items are provided
   */
  onItemSelected: (item: T | null) => void;
};

export const DropdownButton = React.memo(
  <T,>(props: DropdownButtonProps<T>) => {
    const {
      items: items,
      getDisplayTextOfItem,
      getKeyOfItem,
      onItemSelected,
      onClick,
      children,
      ...remainingProps
    } = prepareProps({ props });
    const [displayItems, setDisplayItems] = useState(false);

    const handleItemSelected = useCallback(
      (item: T | null) => {
        setDisplayItems(false);
        onItemSelected(item);
      },
      [onItemSelected, setDisplayItems]
    );

    const handleButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (event) => {
        onClick?.(event);

        if (event.defaultPrevented) {
          return;
        }

        const firstItem = items[0];

        if (items.length > 1) {
          setDisplayItems(true);
        } else if (firstItem) {
          handleItemSelected(firstItem);
        } else {
          handleItemSelected(null);
        }
      },
      [onClick, items, setDisplayItems, handleItemSelected]
    );

    const renderContent: DropdownPopperProps['renderContent'] = useCallback(
      ({ ref }) => (
        <DropdownList
          ref={ref}
          items={items}
          getKeyOfItem={getKeyOfItem}
          getDisplayTextOfItem={getDisplayTextOfItem}
          onItemSelected={handleItemSelected}
          data-testid="DropdownButton.DropdownList"
        ></DropdownList>
      ),
      [items, getKeyOfItem, getDisplayTextOfItem, handleItemSelected]
    );

    return (
      <DropdownPopper
        visible={displayItems}
        renderContent={renderContent}
        setVisible={setDisplayItems}
        hideOnOutsideClick={true}
      >
        {({ ref }) => (
          <ButtonStandard
            ref={ref}
            data-testid="DropdownButton.Button"
            {...remainingProps}
            onClick={handleButtonClick}
          >
            {children}

            {items.length > 1 ? (
              <Icon
                type={IconType.Solid}
                name="caret-down"
                size={IconSize.Small}
                className={styles.icon}
                data-testid="DropdownButton.Icon"
              ></Icon>
            ) : undefined}
          </ButtonStandard>
        )}
      </DropdownPopper>
    );
  }
) as <T>(props: DropdownButtonProps<T>) => JSX.Element;
